import { ActionTree, ActionContext } from 'vuex';

import { fsClient, firewebClient } from '@/http-client';

import { RootStateI } from '../state';
import {
  FirestoreFieldsI,
  FirestoreStateI,
  PathValidationResultI,
  WhereFilterI,
  OrderByI,
} from './state';

export const actions: ActionTree<FirestoreStateI, RootStateI> = {

  async getAllDatabases(
    context: ActionContext<FirestoreStateI, RootStateI>,
    projectId: string,
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.get(`/api/firestore/getalldatabases?projectID=${projectId}`);
    console.log('data', data);
    context.commit('setDatabases', data.databases);
  },

  async getAllCollections(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      databaseId: string;
    },
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.get(`/api/firestore/getallcollections?projectID=${payload.projectId}&databaseID=${payload.databaseId}`);
    context.commit('setCollections', data.collectionIds);
  },

  async getAllDocuments(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      collectionId: string;
      where: WhereFilterI | null;
      orderBy: OrderByI[] | null;
      databaseId: string;
      limit: number;
    },
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.post(`/api/firestore/finddocuments?projectID=${payload.projectId}&databaseID=${payload.databaseId}`, {
      structuredQuery: {
        from: [
          {
            collectionId: payload.collectionId,
          },
        ],
        limit: payload.limit,
        where: payload.where ? payload.where : null,
        orderBy: payload.orderBy ? payload.orderBy : null,
      },
    });
    context.commit('setDocuments', data);
  },

  async getDocument(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      path: string;
      databaseId: string;
    },
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.get(`/api/firestore/getdocument?projectID=${payload.projectId}&path=${payload.path}&databaseID=${payload.databaseId}`);
    context.commit('setDocument', data);
  },

  async getSubcollections(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      path: string;
      databaseId: string;
    },
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.get(`/api/firestore/getsubcollections?projectID=${payload.projectId}&path=${payload.path}&databaseID=${payload.databaseId}`);
    context.commit('setSubcollections', data.collectionIds);
  },

  async getAllDocumentsInSubcollection(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      path: string;
      databaseId: string;
    },
  ) {
    await context.dispatch('auth/refreshToken', null, { root: true });
    const { data } = await firewebClient.get(`/api/firestore/getdocsinsubcollections?projectID=${payload.projectId}&path=${payload.path}&databaseID=${payload.databaseId}`);
    context.commit('setDocumentsInSubcollection', data.documents);
  },

  async saveDocument(
    context: ActionContext<FirestoreStateI, RootStateI>,
    payload: {
      projectId: string;
      path: string;
      fields: FirestoreFieldsI;
      databaseId: string;
    },
  ) {
    console.log('payload', payload);
    await context.dispatch('auth/refreshToken', null, { root: true });
    await firewebClient.post(
      `/api/firestore/savedocument?projectID=${payload.projectId}&path=${payload.path}`,
      payload.fields,
    );
  },

  // verifyPath(
  //   context: ActionContext<FirestoreStateI, RootStateI>,
  //   path: string,
  // ): PathValidationResultI {
  //   const cleanPath = path.replace(/^\/+|\/+$/g, '');
  //   const segments = cleanPath.split('/').filter(Boolean);

  //   // determine path type based on segments length
  //   switch (segments.length) {
  //     case 0:
  //       return {
  //         type: 'databases',
  //         segments,
  //         isValid: true,
  //       };

  //     case 1:
  //       return {
  //         type: 'collections',
  //         segments,
  //         isValid: true,
  //       };

  //     case 2:
  //       return {
  //         type: 'docs',
  //         segments,
  //         isValid: true,
  //       };

  //     case 3:
  //       return {
  //         type: 'doc',
  //         segments,
  //         isValid: true,
  //       };

  //     case 4:
  //       return {
  //         type: 'subcollection',
  //         segments,
  //         isValid: true,
  //       };

  //     case 5:
  //       return {
  //         type: 'doc',
  //         segments,
  //         isValid: true,
  //       };

  //     case 6:
  //       return {
  //         type: 'subcollection',
  //         segments,
  //         isValid: true,
  //       };

  //     case 7:
  //       return {
  //         type: 'doc',
  //         segments,
  //         isValid: true,
  //       };

  //     case 8:
  //       return {
  //         type: 'subcollection',
  //         segments,
  //         isValid: true,
  //       };

  //     case 9:
  //       return {
  //         type: 'doc',
  //         segments,
  //         isValid: true,
  //       };

  //     default: {
  //       throw new Error('Invalid path');
  //     }
  //   }
  // },

  verifyPath(
    context: ActionContext<FirestoreStateI, RootStateI>,
    path: string,
  ): PathValidationResultI {
    const cleanPath = path.replace(/^\/+|\/+$/g, '');
    const segments = cleanPath.split('/').filter(Boolean);
    const segmentCount = segments.length;

    // Firestore paths alternate: collection -> doc -> subcollection -> doc...
    // Max depth is typically 100 segments, but adjust as needed
    if (segmentCount > 100) {
      throw new Error('Invalid path: exceeds maximum depth');
    }

    const getPathType = (count: number): PathValidationResultI['type'] => {
      if (count === 0) return 'databases';
      if (count === 1) return 'collections';
      if (count === 2) return 'docs';

      // Odd numbers (3, 5, 7, 9...) are documents
      // Even numbers (4, 6, 8...) are subcollections
      return count % 2 === 1 ? 'doc' : 'subcollection';
    };

    return {
      type: getPathType(segmentCount),
      segments,
      isValid: true,
    };
  },

};
